import { useEffect } from 'react';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../templates/Section/Section';
import { useTranslation } from '../../i18n/useTranslation';
import { useOnboardingOptional } from './OnboardingContext';

export interface OnboardingSettingsSectionProps {
  className?: string;
}

/**
 * Settings → Introduction: reopen the tour (always possible) and the same
 * "show at start" preference as the checkbox in the tour.
 */
export function OnboardingSettingsSection({ className }: OnboardingSettingsSectionProps) {
  const { t } = useTranslation();
  const onboarding = useOnboardingOptional();
  const loadState = onboarding?.loadState;
  const loadPreference = onboarding?.loadPreference;

  useEffect(() => {
    if (loadState === 'idle') loadPreference?.();
  }, [loadState, loadPreference]);

  if (onboarding === null) return null;
  const { open, showOnStart, setShowOnStart, saveFailed, saving } = onboarding;

  let toggle;
  if (loadState === 'error') {
    toggle = (
      <LoadErrorState
        compact
        titleLevel={3}
        title={t('onboarding.settings.loadError')}
        onRetry={onboarding.loadPreference}
        retryLabel={t('onboarding.settings.retry')}
      />
    );
  } else if (showOnStart === null) {
    toggle = (
      <LiveRegion busy>
        <Text size="sm" color="muted">{t('onboarding.settings.loading')}</Text>
      </LiveRegion>
    );
  } else {
    toggle = (
      <Checkbox
        label={t('onboarding.settings.showOnStart')}
        checked={showOnStart}
        aria-busy={saving || undefined}
        onChange={(event) => void setShowOnStart(event.target.checked)}
        data-testid="onboarding-settings-toggle"
      />
    );
  }

  return (
    <Section title={t('onboarding.settings.title')} className={className}>
      <Stack gap="4">
        <Text color="secondary">{t('onboarding.settings.description')}</Text>
        <div>
          <Button variant="secondary" onClick={open} data-testid="onboarding-settings-open">
            {t('onboarding.settings.reopen')}
          </Button>
        </div>
        {toggle}
        {saveFailed && !onboarding.isOpen && (
          <Alert tone="danger" compact>
            {t('onboarding.saveFailed')}
          </Alert>
        )}
      </Stack>
    </Section>
  );
}

export default OnboardingSettingsSection;
